/**
 * AccountTreeSelect Component
 *
 * A hierarchical account picker with search, keyboard navigation,
 * and recent accounts support. Built with cmdk and Radix UI Popover.
 *
 * @see types/accounting.ts for Account type
 * @see hooks/useRecentAccounts.ts for recent accounts management
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Command } from 'cmdk';
import clsx from 'clsx';
import {
  ChevronDown,
  Search,
  Folder,
  FileText,
  Clock,
  X,
  Check,
} from 'lucide-react';
import type { Account, AccountType } from '@/types/accounting';
import { useRecentAccounts } from '../hooks/useRecentAccounts';

export interface AccountTreeSelectProps {
  /** Flat list of all accounts for lookups */
  accounts: Account[];
  /** Hierarchical tree structure for display */
  accountTree: Account[];
  /** Currently selected account ID */
  value: number | null;
  /** Callback when an account is selected */
  onChange: (accountId: number) => void;
  /** Placeholder text when no account is selected */
  placeholder?: string;
  /** Filter to show only specific account types */
  filterTypes?: AccountType[];
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** Error message to display */
  error?: string;
  /** Additional class names for the trigger button */
  className?: string;
}

/**
 * Recursively filter accounts by type
 */
function filterByTypes(accounts: Account[], types: AccountType[]): Account[] {
  return accounts
    .map((account) => {
      const filteredChildren = account.children
        ? filterByTypes(account.children, types)
        : [];

      // Include if account type matches OR if it has matching children
      const hasMatchingType = types.includes(account.accountType);
      const hasMatchingChildren = filteredChildren.length > 0;

      if (hasMatchingType || hasMatchingChildren) {
        return { ...account, children: filteredChildren };
      }
      return null;
    })
    .filter((account): account is Account => account !== null);
}

/**
 * Recursively filter accounts by search term
 */
function filterBySearch(accounts: Account[], search: string): Account[] {
  const lowerSearch = search.toLowerCase();

  function matches(account: Account): boolean {
    return (
      account.name.toLowerCase().includes(lowerSearch) ||
      account.fullPath.toLowerCase().includes(lowerSearch)
    );
  }

  function filterTree(account: Account): Account | null {
    const childMatches = (account.children || [])
      .map(filterTree)
      .filter((c): c is Account => c !== null);

    if (matches(account) || childMatches.length > 0) {
      return { ...account, children: childMatches };
    }

    return null;
  }

  return accounts.map(filterTree).filter((a): a is Account => a !== null);
}

/**
 * Flatten account tree to a list for keyboard navigation
 */
function flattenTree(accounts: Account[]): Account[] {
  const result: Account[] = [];
  function traverse(accountList: Account[]) {
    for (const account of accountList) {
      result.push(account);
      if (account.children && account.children.length > 0) {
        traverse(account.children);
      }
    }
  }
  traverse(accounts);
  return result;
}

interface AccountTreeItemProps {
  account: Account;
  depth: number;
  onSelect: (accountId: number) => void;
  selectedId: number | null;
  isRecent?: boolean;
}

/**
 * Individual account item in the tree
 */
function AccountTreeItem({
  account,
  depth,
  onSelect,
  selectedId,
  isRecent = false,
}: AccountTreeItemProps) {
  const hasChildren = account.children && account.children.length > 0;
  const isSelected = selectedId === account.id;

  return (
    <Command.Item
      value={account.fullPath}
      onSelect={() => onSelect(account.id)}
      className={clsx(
        'flex items-center gap-2 px-2 py-1.5 cursor-pointer rounded-md',
        'text-sm text-gray-700',
        'aria-selected:bg-blue-50 aria-selected:text-blue-900',
        'hover:bg-gray-50',
        isSelected && 'bg-blue-50'
      )}
      style={{ paddingLeft: `${depth * 16 + 8}px` }}
      data-testid={`account-option-${account.id}`}
    >
      {hasChildren ? (
        <Folder
          className="w-4 h-4 text-gray-400 flex-shrink-0"
          aria-hidden="true"
        />
      ) : (
        <FileText
          className="w-4 h-4 text-gray-400 flex-shrink-0"
          aria-hidden="true"
        />
      )}
      <span className="flex-1 truncate">{account.name}</span>
      {isRecent && (
        <Clock className="w-3 h-3 text-gray-400 flex-shrink-0" aria-hidden="true" />
      )}
      {isSelected && (
        <Check className="w-4 h-4 text-blue-600 flex-shrink-0" aria-hidden="true" />
      )}
    </Command.Item>
  );
}

interface AccountTreeGroupProps {
  account: Account;
  depth: number;
  onSelect: (accountId: number) => void;
  selectedId: number | null;
}

/**
 * Recursive component for rendering account tree groups
 */
function AccountTreeGroup({
  account,
  depth,
  onSelect,
  selectedId,
}: AccountTreeGroupProps) {
  return (
    <>
      <AccountTreeItem
        account={account}
        depth={depth}
        onSelect={onSelect}
        selectedId={selectedId}
      />
      {account.children?.map((child) => (
        <AccountTreeGroup
          key={child.id}
          account={child}
          depth={depth + 1}
          onSelect={onSelect}
          selectedId={selectedId}
        />
      ))}
    </>
  );
}

/**
 * AccountTreeSelect - A hierarchical account picker component.
 *
 * Features:
 * - Search/filter by name or full path
 * - Hierarchical display with indentation
 * - Keyboard navigation (arrow keys, Enter, Escape)
 * - Recent accounts section
 * - Account type filtering
 * - Accessible with ARIA labels
 *
 * @example
 * ```tsx
 * <AccountTreeSelect
 *   accounts={accounts}
 *   accountTree={accountTree}
 *   value={selectedAccountId}
 *   onChange={setSelectedAccountId}
 *   filterTypes={['expense']}
 *   placeholder="Select expense account..."
 * />
 * ```
 */
export function AccountTreeSelect({
  accounts,
  accountTree,
  value,
  onChange,
  placeholder = 'Select account...',
  filterTypes,
  disabled = false,
  error,
  className,
}: AccountTreeSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { recentAccountIds, addRecentAccount } = useRecentAccounts();

  // Find selected account
  const selectedAccount = useMemo(
    () => accounts.find((a) => a.id === value) ?? null,
    [accounts, value]
  );

  // Handle account selection
  const handleSelect = useCallback(
    (accountId: number) => {
      onChange(accountId);
      addRecentAccount(accountId);
      setOpen(false);
      setSearch('');
    },
    [onChange, addRecentAccount]
  );

  // Filter accounts based on type and search
  const filteredAccounts = useMemo(() => {
    let filtered = accountTree;

    if (filterTypes && filterTypes.length > 0) {
      filtered = filterByTypes(filtered, filterTypes);
    }

    if (search) {
      filtered = filterBySearch(filtered, search);
    }

    return filtered;
  }, [accountTree, filterTypes, search]);

  // Get recent accounts that exist and match filter types
  const recentAccounts = useMemo(() => {
    return recentAccountIds
      .map((id) => accounts.find((a) => a.id === id))
      .filter((account): account is Account => {
        if (!account) return false;
        if (!filterTypes || filterTypes.length === 0) return true;
        return filterTypes.includes(account.accountType);
      });
  }, [recentAccountIds, accounts, filterTypes]);

  // Focus search input when popover opens
  useEffect(() => {
    if (open && inputRef.current) {
      // Small delay to ensure popover is rendered
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setSearch('');
      }
    },
    []
  );

  const showRecentAccounts = recentAccounts.length > 0 && !search;
  const hasResults = filteredAccounts.length > 0;

  return (
    <div className="relative">
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={clsx(
              'w-full flex items-center justify-between gap-2',
              'px-3 py-2 text-left text-sm',
              'border rounded-md shadow-sm',
              'bg-white',
              'transition-colors duration-150',
              disabled
                ? 'bg-gray-50 text-gray-500 cursor-not-allowed'
                : 'hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
              error
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300',
              className
            )}
            disabled={disabled}
            aria-label={selectedAccount ? `Selected: ${selectedAccount.fullPath}` : placeholder}
            aria-expanded={open}
            aria-haspopup="listbox"
            data-testid="account-tree-select-trigger"
          >
            <span
              className={clsx(
                'truncate flex-1',
                !selectedAccount && 'text-gray-500'
              )}
            >
              {selectedAccount ? selectedAccount.fullPath : placeholder}
            </span>
            <ChevronDown
              className={clsx(
                'w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200',
                open && 'rotate-180'
              )}
              aria-hidden="true"
            />
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            className={clsx(
              'w-80 bg-white rounded-lg shadow-lg border border-gray-200',
              'z-50',
              'animate-in fade-in-0 zoom-in-95'
            )}
            sideOffset={4}
            align="start"
            onKeyDown={handleKeyDown}
            data-testid="account-tree-select-popover"
          >
            <Command
              className="flex flex-col"
              shouldFilter={false}
              loop
            >
              {/* Search input */}
              <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200">
                <Search
                  className="w-4 h-4 text-gray-400 flex-shrink-0"
                  aria-hidden="true"
                />
                <Command.Input
                  ref={inputRef}
                  value={search}
                  onValueChange={setSearch}
                  placeholder="Search accounts..."
                  className={clsx(
                    'flex-1 bg-transparent text-sm',
                    'placeholder:text-gray-400',
                    'focus:outline-none'
                  )}
                  data-testid="account-tree-select-search"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="p-0.5 rounded hover:bg-gray-100"
                    aria-label="Clear search"
                  >
                    <X className="w-3 h-3 text-gray-400" />
                  </button>
                )}
              </div>

              {/* Account list */}
              <Command.List
                className="max-h-80 overflow-y-auto p-1"
                data-testid="account-tree-select-list"
              >
                {/* Empty state */}
                <Command.Empty className="py-6 text-center text-sm text-gray-500">
                  No accounts found.
                </Command.Empty>

                {/* Recent accounts section */}
                {showRecentAccounts && (
                  <Command.Group className="pb-1">
                    <div className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Recent
                    </div>
                    {recentAccounts.map((account) => (
                      <AccountTreeItem
                        key={`recent-${account.id}`}
                        account={account}
                        depth={0}
                        onSelect={handleSelect}
                        selectedId={value}
                        isRecent
                      />
                    ))}
                    <div className="my-1 border-t border-gray-100" />
                  </Command.Group>
                )}

                {/* Account tree */}
                {hasResults && (
                  <Command.Group>
                    {filteredAccounts.map((rootAccount) => (
                      <AccountTreeGroup
                        key={rootAccount.id}
                        account={rootAccount}
                        depth={0}
                        onSelect={handleSelect}
                        selectedId={value}
                      />
                    ))}
                  </Command.Group>
                )}
              </Command.List>
            </Command>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export default AccountTreeSelect;

// Export helper functions for testing
export { filterByTypes, filterBySearch, flattenTree };
